import { put, takeLatest, select, take, race, takeEvery } from 'redux-saga/effects';
import types from 'redux/types';

import NotificationService from 'services/NotificationServices';
import { showError } from 'helpers/handleError';
import { getNextDataRefetch } from 'helpers';
import { notifySuccess } from 'helpers/handleNotification';
import components, { typeUnmounted } from 'consts/components';

const checkIsLast = (list = [], limit) => {
  return list.length < limit;
};

function* getNotification(data) {
  const { filters } = data;
  const { limit, status, offset } = filters;
  const isUnread = !status;
  try {
    const { res, cancelled } = yield race({
      res: NotificationService.getNotification({ limit, offset, status }),
      cancelled: take(typeUnmounted(components.notifications)),
    });

    if (cancelled) {
      const notifications = yield select((state) => state.notificationReducer.notifications);
      const isLast = yield select((state) => state.notificationReducer.isLast);
      yield put({ type: types.REQUEST_GET_NOTIFICATION_SUCCESS, notifications, isLast, isUnread });
      return;
    }

    const notifications = res?.data?.notifications || [];
    const isLast = checkIsLast(notifications, limit);
    yield put({ type: types.REQUEST_GET_NOTIFICATION_SUCCESS, notifications, isLast, isUnread });
  } catch (error) {
    showError(error);
    yield put({
      type: types.REQUEST_GET_NOTIFICATION_FAILED,
      error,
    });
  }
}

function* refetchNotification(data) {
  const { filters } = data;
  const { limit, status, page } = filters;
  const isUnread = !status;
  try {
    let listRequest = [];
    for (let i = 0; i < page; i++) {
      listRequest = [
        ...listRequest,
        NotificationService.getNotification({ limit, offset: i * limit, status }),
      ];
    }

    const { res, cancelled } = yield race({
      res: Promise.all(listRequest),
      cancelled: take(typeUnmounted(components.notifications)),
    });

    if (cancelled) {
      const notifications = yield select((state) => state.notificationReducer.notifications);
      const isLast = yield select((state) => state.notificationReducer.isLast);
      yield put({ type: types.REQUEST_GET_NOTIFICATION_SUCCESS, notifications, isLast, isUnread });
      return;
    }

    const [nextData, isLast] = getNextDataRefetch(res, limit, 'notifications');
    yield put({
      type: types.REQUEST_GET_NOTIFICATION_SUCCESS,
      notifications: nextData,
      isLast,
      isUnread,
    });
  } catch (error) {
    showError(error);
    yield put({
      type: types.REQUEST_GET_NOTIFICATION_FAILED,
      error,
    });
  }
}

function* updateNotification(data) {
  const { notifications, status, filters } = data;
  try {
    yield NotificationService.updateNotification({ notifications, status });

    notifySuccess('Mark as read successfully!');
    yield put({ type: types.REQUEST_UPDATE_NOTIFICATION_SUCCESS, notifications });
    yield put({ type: types.REQUEST_RE_FETCH_NOTIFICATION, filters });
  } catch (error) {
    showError(error);
    yield put({
      type: types.REQUEST_UPDATE_NOTIFICATION_FAILED,
      notifications,
      error,
    });
  }
}

function* loadMoreNotification(data) {
  const { filters } = data;
  const { limit, status, offset } = filters;
  const isUnread = !status;
  try {
    const { res, cancelled } = yield race({
      res: NotificationService.getNotification({ limit, offset, status }),
      cancelled: take(typeUnmounted(components.notifications)),
    });

    if (cancelled) {
      const notifications = yield select((state) => state.notificationReducer.notifications);
      const isLast = yield select((state) => state.notificationReducer.isLast);
      yield put({
        type: types.REQUEST_LOAD_MORE_NOTIFICATION_SUCCESS,
        notifications,
        isLast,
        isUnread,
      });
      return;
    }

    const notifications = res?.data?.notifications || [];
    const isLast = checkIsLast(notifications, limit);
    yield put({
      type: types.REQUEST_LOAD_MORE_NOTIFICATION_SUCCESS,
      notifications,
      isLast,
      isUnread,
    });
  } catch (error) {
    showError(error);
    yield put({
      type: types.REQUEST_LOAD_MORE_NOTIFICATION_FAILED,
      error,
    });
  }
}

function* markAllAsRead(data) {
  const { filters } = data;
  try {
    yield NotificationService.markAllAsRead();
    notifySuccess('Mark all as read successfully!');
    yield put({ type: types.REQUEST_MARK_AS_ALL_READ_SUCCESS });
    yield put({ type: types.REQUEST_RE_FETCH_NOTIFICATION, filters });
  } catch (error) {
    showError(error);
    yield put({ type: types.REQUEST_MARK_AS_ALL_READ_FAILED, error });
  }
}

export default function* watchAPIFunction() {
  yield takeLatest(types.REQUEST_GET_NOTIFICATION, getNotification);
  yield takeEvery(types.REQUEST_UPDATE_NOTIFICATION, updateNotification);
  yield takeLatest(types.REQUEST_RE_FETCH_NOTIFICATION, refetchNotification);
  yield takeLatest(types.REQUEST_LOAD_MORE_NOTIFICATION, loadMoreNotification);
  yield takeLatest(types.REQUEST_MARK_AS_ALL_READ, markAllAsRead);
}
